import { useEffect } from 'react';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import {
  Box,
  Autocomplete,
  TextField,
  Divider,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Card,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Civil3dMapping, PropertyMappingForm } from './PropertyMappingsForm';
import { Civil3dPropertyApiModel } from '../../../../../types/models/civil3d-property.model';
import {
  Civil3dPropertySetApiModel,
  WithCivil3dProperties,
} from '../../../../../types/models/civil3d-property-set.model';
import { useConfirm } from '../../../contexts/confirm.context';
import { AttributeApiModel } from '../../../../../types/models/attribute.model';
import { Civil3dPropertyType } from '../../../../../types/constants/civil3d-property-type';

type Props = {
  propertyMappings: {
    civil3dPropertyId: number;
    attributeId: number;
  }[];
  objectPropertySets: (Civil3dPropertySetApiModel & WithCivil3dProperties)[];
  propertySets: (Civil3dPropertySetApiModel & WithCivil3dProperties)[];
  userDefinedPropertySets: (Civil3dPropertySetApiModel & WithCivil3dProperties)[];
  attributes: Pick<AttributeApiModel, 'id' | 'name' | 'type'>[];
  onSubmit: SubmitHandler<PropertyMappingForm>;
  onSubmittingChange: (isSubmitting: boolean) => void;
  onGetAvailableAttributes: (
    type: Civil3dPropertyType,
    usedAttributes?: number[],
  ) => (Pick<AttributeApiModel, 'id' | 'name' | 'type'> & { isDisabled?: boolean })[] | undefined;
};

export const PropertyMappingRenderer = ({
  propertyMappings,
  objectPropertySets,
  propertySets,
  userDefinedPropertySets,
  attributes,
  onSubmit,
  onSubmittingChange,
  onGetAvailableAttributes,
}: Props) => {
  const {
    control,
    register,
    watch,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PropertyMappingForm>({
    resolver: classValidatorResolver(PropertyMappingForm),
    defaultValues: {
      propertyMappings,
    },
  });

  const [showConfirm] = useConfirm();

  const currentPropertyMappings = watch('propertyMappings');

  const allPropertySets = [...objectPropertySets, ...propertySets, ...userDefinedPropertySets];

  const propertyIds = allPropertySets.flatMap(({ properties }) => properties.map(({ id }) => id));

  const propertyIdToIndexMap = new Map(
    currentPropertyMappings.map(({ civil3dPropertyId }, index) => [civil3dPropertyId, index]),
  );

  const missingPropertyMappings = propertyIds
    .filter((propertyId) => !propertyIdToIndexMap.has(propertyId))
    .map((civil3dPropertyId) => ({ civil3dPropertyId, attributeId: 0 }));

  const propertyMappingsWithEmptyValues = [...currentPropertyMappings, ...missingPropertyMappings];

  const propertyIdToMappingIndexMap = new Map(
    propertyMappingsWithEmptyValues.map(({ civil3dPropertyId }, index) => [civil3dPropertyId, index]),
  );

  useEffect(() => {
    onSubmittingChange(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const calculateAvailableAttributes = (type: Civil3dPropertyType, currentAttributeId?: number) => {
    const latestPropertyMappings = getValues('propertyMappings');
    const usedAttributes = latestPropertyMappings
      .filter(Boolean)
      .map(({ attributeId }) => attributeId)
      .filter((id) => id !== currentAttributeId);

    const availableAttributes = onGetAvailableAttributes(type, usedAttributes) ?? [];

    if (currentAttributeId && currentAttributeId !== 0) {
      const hasCurrentValue = availableAttributes.some(({ id }) => id === currentAttributeId);

      if (!hasCurrentValue) {
        const currentAttribute = attributes?.find(({ id }) => id === currentAttributeId);
        if (currentAttribute) {
          availableAttributes.push({ ...currentAttribute, isDisabled: false });
        }
      }
    }

    return availableAttributes.sort((a, b) => a.type.localeCompare(b.type));
  };

  const handleMapPropertiesAsNamed = (properties: Civil3dPropertyApiModel[], propertyMappingValue: Civil3dMapping[]) =>
    properties.reduce(
      (
        propertyMappingsAcc,
        { id: civil3dPropertyId, name: propertyName, displayName: propertyDisplayName, type: propertyType },
      ) => {
        const availableAttributes = calculateAvailableAttributes(propertyType);
        const matchedAttribute = availableAttributes.find(({ name }) => name === (propertyDisplayName ?? propertyName));

        const isAttributeAlreadyInUse = propertyMappingValue.some(
          ({ attributeId }) => attributeId === matchedAttribute?.id,
        );

        if (!matchedAttribute || isAttributeAlreadyInUse) {
          return propertyMappingsAcc;
        }
        return [...propertyMappingsAcc, { civil3dPropertyId, attributeId: matchedAttribute.id }];
      },
      [],
    );

  const handleMapPropertiesAsNamedAction = () => {
    showConfirm({
      onConfirm: async () => {
        // Fixes getting stale values from the watch
        const propertyMappingValue = getValues('propertyMappings');

        const sameNamePropertySetMappings = allPropertySets.reduce((acc, { properties }) => {
          const sameNamePropertyMappings = handleMapPropertiesAsNamed(properties, propertyMappingValue);
          return [...acc, ...sameNamePropertyMappings];
        }, []);

        const uniqueSameNameMappings = sameNamePropertySetMappings.filter(
          ({ attributeId }, index, self) =>
            self.findIndex((propertyMapping) => propertyMapping.attributeId === attributeId) === index,
        );

        setValue(
          'propertyMappings',
          propertyMappingValue.map((mapping) => {
            const sameNameMapping = uniqueSameNameMappings.find(
              ({ civil3dPropertyId }) => civil3dPropertyId === mapping.civil3dPropertyId,
            );
            return !mapping?.attributeId && sameNameMapping ? sameNameMapping : mapping;
          }),
          { shouldValidate: true, shouldDirty: true, shouldTouch: true },
        );
      },

      title: `Overwrite Property Mappings To Match Property Names`,
      message: `Are you sure you want to match property mappings to attributes with the same name (if applicable)? This will overwrite the existing mappings for these properties.`,
    });
  };

  const renderProperties = (properties: Civil3dPropertyApiModel[]) => (
    <Box>
      {properties
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ id, name: propertyName, displayName: propertyDisplayName, type: propertyType }) => {
          const propertyMappingIndex = propertyIdToMappingIndexMap.get(id);
          if (propertyMappingIndex === undefined) return null;

          return (
            <Box key={propertyName}>
              <Box sx={{ py: 0.5 }}>
                <input
                  type="hidden"
                  {...register(`propertyMappings.${propertyMappingIndex}.civil3dPropertyId`, {
                    valueAsNumber: true,
                  })}
                  value={id}
                />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ width: '50%' }}>{propertyDisplayName ?? propertyName}</Typography>
                  <Box flexGrow={1}>
                    <Controller
                      control={control}
                      name={`propertyMappings.${propertyMappingIndex}.attributeId`}
                      render={({ field }) => {
                        const options = calculateAvailableAttributes(propertyType, field.value);

                        return (
                          <Autocomplete
                            size="small"
                            onChange={(_event, selectedOption) => field.onChange(selectedOption?.id ?? 0)}
                            value={options.find((attr) => attr.id === field.value) ?? null}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            onBlur={field.onBlur}
                            options={options}
                            getOptionDisabled={({ isDisabled }) => isDisabled === true}
                            getOptionLabel={({ name }) => name}
                            groupBy={({ type }) => type}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={Boolean(errors.propertyMappings?.[propertyMappingIndex]?.attributeId)}
                                helperText={errors.propertyMappings?.[propertyMappingIndex]?.attributeId?.message}
                              />
                            )}
                          />
                        );
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              <Divider />
            </Box>
          );
        })}
    </Box>
  );

  const renderPropertySets = (
    title: string,
    civil3dPropertySets: (Civil3dPropertySetApiModel & WithCivil3dProperties)[],
    isObjectProperties = false,
  ) => (
    <Box>
      <Typography sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>{title}</Typography>
      {civil3dPropertySets?.map(({ name: propertySetName, properties }) =>
        isObjectProperties ? (
          <Card key={propertySetName} variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 20, mb: 2 }}>
              <Typography sx={{ fontWeight: 'bold' }}>Civil 3D Property</Typography>
              <Typography sx={{ fontWeight: 'bold' }}>ArcGIS Attribute</Typography>
            </Box>
            <Divider />
            {renderProperties(properties)}
          </Card>
        ) : (
          <Accordion key={propertySetName}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography component="span">{propertySetName}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', gap: 20, mb: 2 }}>
                <Typography sx={{ fontWeight: 'bold' }}>Civil 3D Property</Typography>
                <Typography sx={{ fontWeight: 'bold' }}>ArcGIS Attribute</Typography>
              </Box>
              <Divider />
              {renderProperties(properties)}
            </AccordionDetails>
          </Accordion>
        ),
      )}
    </Box>
  );

  return (
    <form id="property-mappings-form" onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ maxHeight: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Link component="button" type="button" disabled={isSubmitting} onClick={handleMapPropertiesAsNamedAction}>
            Map as Named
          </Link>
        </Box>
        {renderPropertySets('Object Properties', objectPropertySets, true)}
        {renderPropertySets('Property Sets', propertySets)}
        {renderPropertySets('User-Defined Properties', userDefinedPropertySets)}
      </Box>
    </form>
  );
};
